import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  CustomerAccountHeaderVm,
  CustomerAccountLinkHeaderVm,
  CustomerAccountLinkTypesEnum,
  CustomerDetailVm,
  CustomerDto,
  CustomerHeaderVm,
} from '@/api';
import isDefined from '@common/utils/isDefined';

type LocalCustomer = {
  firstName: string;
  lastName: string;
  id: string;
  external: boolean;
  isFake: boolean;
  parentId: string;
  email?: string;
  birthDay?: string;
};

const emptyCustomer: LocalCustomer = {
  firstName: '',
  lastName: '',
  id: '',
  external: false,
  isFake: false,
  parentId: '',
};

export const emptyCustodianData: CustomerDto = {
  id: '',
  firstName: '',
  lastName: '',
  fullName: '',
  birthDay: '',
  email: '',
  phoneNumber: '',
  address: {
    street: '',
    streetNumber: '',
    city: '',
    zip: '',
  },
  draft: false,
  originId: '',
  external: false,
  isFake: false,
};

export const useCustomerStore = defineStore('customer', () => {
  const customer = ref({ ...emptyCustomer });
  const isChild = ref(false);
  const canChangeChildAccountToRegularAccount = ref(false);

  /**
   * Used to show detail of assigned/managed account from AssignedCardListPage.vue and ManagedCardListPage.vue
   */
  const linkedAccountCustomer = ref<{
    customer: LocalCustomer;
    linkType: CustomerAccountLinkTypesEnum;
  }>({
    customer: { ...emptyCustomer },
    linkType: CustomerAccountLinkTypesEnum.ASSIGNED,
  });

  const customerConnected = computed(() => {
    return !!customer.value.id;
  });

  const linkedCustomer = computed(() => {
    return linkedAccountCustomer.value.customer;
  });

  const currentlySelectedCustomerId = computed(() => {
    return isLinkedCustomerSelected.value ? linkedCustomer.value.id : customer.value.id;
  });

  const currentlySelectedCustomer = computed(() => {
    return isLinkedCustomerSelected.value ? linkedCustomer.value : customer.value;
  });

  const registeredCustomer = computed(() => {
    return customer.value;
  });

  const registeredCustomerIsChild = computed(() => {
    return isChild.value;
  });

  const childAccountChangeEnabled = computed(() => {
    return canChangeChildAccountToRegularAccount.value;
  });

  const isLinkedCustomerSelected = computed(() => {
    return isDefined(linkedCustomer.value.id);
  });

  const isManagedCustomerSelected = computed(() => {
    return (
      isLinkedCustomerSelected.value && linkedAccountCustomer.value.linkType === CustomerAccountLinkTypesEnum.MANAGED
    );
  });

  const isAssignedCustomerSelected = computed(() => {
    return (
      isLinkedCustomerSelected.value && linkedAccountCustomer.value.linkType === CustomerAccountLinkTypesEnum.ASSIGNED
    );
  });

  const hasLoggedInAccountCustomer = computed(() => {
    return !!customer.value.id;
  });

  const setCustomer = (data: CustomerHeaderVm | CustomerDetailVm | CustomerDto) => {
    customer.value = {
      firstName: data.firstName ?? '',
      lastName: data.lastName ?? '',
      id: data.id,
      external: data.external,
      isFake: data.isFake ?? false,
      parentId: data.parentId ?? '',
      email: 'accountEmail' in data ? (data.accountEmail ?? undefined) : undefined,
      birthDay: data.birthDay ?? undefined,
    };
  };

  const setCustomerAccountHeader = (customerAccountHeader: CustomerAccountHeaderVm) => {
    customer.value = {
      firstName: customerAccountHeader.customer?.firstName ?? '',
      lastName: customerAccountHeader.customer?.lastName ?? '',
      id: customerAccountHeader.customer?.id,
      external: customerAccountHeader.customer?.external ?? false,
      isFake: customerAccountHeader.customer?.isFake ?? false,
      parentId: customerAccountHeader.customer?.parentId ?? '',
    };
    isChild.value = customerAccountHeader.isChild;
    canChangeChildAccountToRegularAccount.value = customerAccountHeader.canChangeChildAccountToRegularAccount;
  };

  const setChildAccountChangeDisabled = () => {
    canChangeChildAccountToRegularAccount.value = false;
  };

  const setAssignedAccountCustomer = (link: CustomerAccountLinkHeaderVm) =>
    setLinkedAccountCustomer({
      ...link,
      linkType: CustomerAccountLinkTypesEnum.ASSIGNED,
    });

  const setManagedAccountCustomer = (link: CustomerAccountLinkHeaderVm) =>
    setLinkedAccountCustomer({
      ...link,
      linkType: CustomerAccountLinkTypesEnum.MANAGED,
    });

  const setLinkedAccountCustomer = (link: CustomerAccountLinkHeaderVm) => {
    linkedAccountCustomer.value.customer.id = link.customer.id;
    linkedAccountCustomer.value.customer.firstName = link.customer.firstName ?? '';
    linkedAccountCustomer.value.customer.lastName = link.customer.lastName ?? '';
    linkedAccountCustomer.value.customer.external = link.customer.external;
    linkedAccountCustomer.value.customer.isFake = link.customer.isFake ?? false;
    linkedAccountCustomer.value.linkType = link.linkType as CustomerAccountLinkTypesEnum;
  };

  const clearLinkedCustomer = () => {
    // if the linked account customer is clear, the main account will always be selected
    linkedAccountCustomer.value = {
      customer: { ...emptyCustomer },
      linkType: CustomerAccountLinkTypesEnum.ASSIGNED,
    };
  };

  const clearCustomer = () => {
    customer.value = { ...emptyCustomer };
  };

  return {
    customer,
    isChild,
    canChangeChildAccountToRegularAccount,

    linkedCustomer,
    customerConnected,
    currentlySelectedCustomer,
    currentlySelectedCustomerId,
    registeredCustomer,
    registeredCustomerIsChild,
    childAccountChangeEnabled,
    isManagedCustomerSelected,
    isAssignedCustomerSelected,
    hasLoggedInAccountCustomer,
    isLinkedCustomerSelected,

    setCustomer,
    setCustomerAccountHeader,
    setChildAccountChangeDisabled,
    setAssignedAccountCustomer,
    setManagedAccountCustomer,
    setLinkedAccountCustomer,
    clearLinkedCustomer,
    clearCustomer,
  };
});
